import { fabClasses, type FabProps } from '@mui/material/Fab';
import { alpha, type Components, lighten, type Theme } from '@mui/material/styles';

import { getAccessibleTextColor } from 'src/theme/getAccessibleTextColor';

import getStyleQuery from '../../../utils/tools/getStyleQuery';

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

// NEW VARIANT
declare module '@mui/material/Fab' {
    interface FabPropsVariantOverrides {
        outlined: true;
        outlinedExtended: true;
        soft: true;
        softExtended: true;
    }
}

// ----------------------------------------------------------------------

export function fab(theme: Theme): Components<Theme> {
    const lightMode = theme.palette.mode === 'light';

    const rootStyles = (ownerState: FabProps) => {
        const defaultColor = ownerState.color === 'default';

        const inheritColor = ownerState.color === 'inherit';

        const circularVariant = ownerState.variant === 'circular';

        const extendedVariant = ownerState.variant === 'extended';

        const outlinedVariant = ownerState.variant === 'outlined';

        const outlinedExtendedVariant = ownerState.variant === 'outlinedExtended';

        const softVariant = ownerState.variant === 'soft';

        const softExtendedVariant = ownerState.variant === 'softExtended';

        const defaultStyle = {
            '&:hover, &:active': {
                boxShadow: theme.customShadows.z8,
            },
            // FILLED
            ...((circularVariant || extendedVariant) && {
                ...((defaultColor || inheritColor) && {
                    boxShadow: 'none',
                }),
                ...(inheritColor && {
                    backgroundColor: theme.palette.text.primary,
                    color: lightMode ? theme.palette.common.white : theme.palette.grey[800],
                    '&:hover': {
                        backgroundColor: lightMode ? theme.palette.grey[700] : theme.palette.grey[400],
                    },
                }),
            }),
            // OUTLINED
            ...((outlinedVariant || outlinedExtendedVariant) && {
                boxShadow: 'none',
                backgroundColor: 'transparent',
                ...((defaultColor || inheritColor) && {
                    border: `solid 1px ${alpha(theme.palette.grey[500], 0.32)}`,
                }),
                ...(defaultColor && {
                    ...(!lightMode && {
                        color: theme.palette.text.secondary,
                    }),
                }),

                '&:hover': {
                    borderColor: 'currentColor',
                    boxShadow: '0 0 0 0.5px currentColor',
                    backgroundColor: theme.palette.action.hover,
                },
            }),
            // SOFT
            ...((softVariant || softExtendedVariant) && {
                boxShadow: 'none',
                ...(defaultColor && {
                    color: theme.palette.grey[800],
                    backgroundColor: theme.palette.grey[300],
                    '&:hover': {
                        backgroundColor: theme.palette.grey[400],
                    },
                }),
                ...(inheritColor && {
                    backgroundColor: lighten(theme.palette.grey[500], 0.6),
                    '&:hover': {
                        backgroundColor: lighten(theme.palette.grey[500], 0.4),
                    },
                }),
            }),
        };

        const colorStyle = COLORS.map((color) => {
            const baseBgColor = lighten(theme.palette[color].main, 0.85);
            const baseTextColor = theme.palette[color][lightMode ? 'dark' : 'light'];

            // Adjust text color if contrast is too low
            const accessibleTextColor = getAccessibleTextColor(theme, baseBgColor, baseTextColor);

            return {
                ...(ownerState.color === color && {
                    // FILLED
                    ...((circularVariant || extendedVariant) && {
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: theme.palette[color].dark,
                            boxShadow: theme.customShadows[color],
                        },
                    }),
                    // OUTLINED
                    ...((outlinedVariant || outlinedExtendedVariant) && {
                        color: theme.palette[color].main,
                        border: `solid 1px ${alpha(theme.palette[color].main, 0.48)}`,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette[color].main, 0.08),
                        },
                    }),
                    // SOFT
                    ...((softVariant || softExtendedVariant) && {
                        color: accessibleTextColor,
                        backgroundColor: baseBgColor,
                        '&:hover': {
                            backgroundColor: lighten(theme.palette[color].main, 0.75),
                        },
                    }),
                }),
            };
        });

        const disabledState = {
            [`&.${fabClasses.disabled}`]: {
                ...((outlinedVariant || outlinedExtendedVariant) && {
                    backgroundColor: 'transparent',
                    border: `solid 1px ${theme.palette.action.disabledBackground}`,
                }),
                // SOFT
                ...(softVariant && {
                    backgroundColor: theme.palette.action.disabledBackground,
                }),
            },
        };

        const size = {
            ...((extendedVariant || outlinedExtendedVariant || softExtendedVariant) && {
                width: 'auto',
                '& svg': {
                    marginRight: theme.spacing(1),
                },
                ...(ownerState.size === 'small' && {
                    height: 34,
                    minHeight: 34,
                    borderRadius: 17,
                    padding: theme.spacing(0, 1),
                }),
                ...(ownerState.size === 'medium' && {
                    height: 40,
                    minHeight: 40,
                    borderRadius: 20,
                    padding: theme.spacing(0, 2),
                }),
                ...(ownerState.size === 'large' && {
                    height: 48,
                    minHeight: 48,
                    borderRadius: 24,
                    padding: theme.spacing(0, 2),
                }),
            }),
        };

        return [defaultStyle, ...colorStyle, disabledState, size];
    };

    return {
        MuiFab: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: FabProps }) => ({
                    [getStyleQuery()]: rootStyles(ownerState),
                }),
            },
        },
    };
}
